import { useNavigate, useRouter, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useWatch, type FieldErrors, type FieldPath, type FieldValues, type UseFormReturn } from 'react-hook-form';

import { applyApiFieldErrors } from '@/shared/form';
import { ApiError, useCurrentTenant, type FieldError } from '@/shared/http';

import { backStepsOf, FLOW_PARAM, firstIncomplete, isReachable, stepOfField, visibleSteps, type FlowDefinition, type FlowStep } from './define-flow';
import { createFlowDraft, flowDraftKey, mergeDraft, type FlowDraft } from './flow-draft';

export interface FlowDraftOptions<V extends FieldValues = FieldValues> {
  /**
   * Lista de permissão: só estes caminhos (`a.b.c`) vão ao `sessionStorage`. Senha, código/TOTP,
   * documento (CPF/CNPJ) e arquivo são recusados mesmo declarados (`isSensitiveDraftPath`).
   */
  readonly include: readonly FieldPath<V>[];
  /** `sub` de quem está logado: `shared` ainda não tem porta de sessão, a tela informa. Sem ele, não grava. */
  readonly user: string | undefined;
  /** Correspondente; padrão `useCurrentTenant()`. Sem ele, não grava. */
  readonly tenant?: string | undefined;
}

/**
 * Rascunho do fluxo no `sessionStorage` da aba (some ao fechar), em `uyzy.flow.<fluxo>.<tenant>.<sub>`:
 * com ele, o F5 volta ao mesmo passo com os valores declarados. Sem usuário ou tenant, `undefined`.
 */
export function useFlowDraft<V extends FieldValues>(flowId: string, options: FlowDraftOptions<V> | undefined): FlowDraft | undefined {
  const currentTenant = useCurrentTenant();
  const key = options ? flowDraftKey(flowId, options.tenant ?? currentTenant, options.user) : undefined;
  const include = options?.include.join('|') ?? '';
  return useMemo(() => (key === undefined ? undefined : createFlowDraft(key, include === '' ? [] : include.split('|'))), [key, include]);
}

/** Um erro de campo que a tela não mostra: o passo dele está escondido ou ele não é de nenhum passo. */
export interface FlowOrphanError {
  readonly name: string;
  readonly message: string;
}

function flattenErrors(errors: unknown, prefix = ''): FlowOrphanError[] {
  if (errors === null || typeof errors !== 'object') return [];
  return Object.entries(errors).flatMap(([key, value]): FlowOrphanError[] => {
    if (key === 'ref' || value === null || typeof value !== 'object') return [];
    const name = prefix === '' ? key : `${prefix}.${key}`;
    const leaf = value as { readonly type?: unknown; readonly message?: unknown };
    if (typeof leaf.type === 'string') return [{ name, message: typeof leaf.message === 'string' && leaf.message !== '' ? leaf.message : name }];
    return flattenErrors(value, name);
  });
}

/** Os erros do formulário cujo campo não está em nenhum passo visível (R2 da revisão do !11135). */
export function orphanFieldErrors<V extends FieldValues>(errors: FieldErrors<V>, visible: readonly FlowStep<V>[]): FlowOrphanError[] {
  return flattenErrors(errors).filter((error) => !stepOfField(visible, error.name));
}

export interface FlowStepError {
  readonly error: ApiError;
  /** Erros de campo da API que não casaram com nenhum campo. */
  readonly unmatched: readonly FieldError[];
}

export interface FlowModel<V extends FieldValues> {
  readonly definition: FlowDefinition<V>;
  readonly form: UseFormReturn<V>;
  readonly param: string;
  /** Passos visíveis para os valores atuais. */
  readonly steps: readonly FlowStep<V>[];
  readonly current: FlowStep<V>;
  /** Posição do passo atual entre os visíveis (0…). */
  readonly index: number;
  readonly total: number;
  readonly isFirst: boolean;
  readonly isReview: boolean;
  /** Somente leitura: todo passo abre e o avançar não valida nem roda o `beforeNext`. */
  readonly readOnly: boolean;
  readonly completed: ReadonlySet<string>;
  /** Sentido da última troca, para o par `drill` (U38). */
  readonly direction: 'forward' | 'back';
  /** O `beforeNext` do passo está rodando. */
  readonly pending: boolean;
  readonly error: FlowStepError | undefined;
  /** Muda a cada tentativa inválida: a tela foca o primeiro campo marcado. */
  readonly errorEpoch: number;
  /** Valida o passo, roda o `beforeNext` e avança. `true` quando avançou. */
  readonly next: () => Promise<boolean>;
  /** Volta `steps` passos visíveis (padrão: o `backSteps` do passo, ou 1), rodando o `onBack` dele. */
  readonly back: (steps?: number) => void;
  /** Vai a um passo já alcançável (os anteriores concluídos). */
  readonly goTo: (id: string) => void;
  readonly canGoTo: (id: string) => boolean;
  /**
   * Tira o passo e os seguintes de `completed`, para o `beforeNext` rodar de novo. Para quando um campo muda
   * por `setValue` (busca, sheet), que o fluxo não ouve: só a digitação (`type === 'change'`) reabre sozinha.
   */
  readonly reopen: (stepId: string) => void;
  /** Passo visível dono do primeiro campo com erro, para voltar a ele depois de uma recusa. */
  readonly stepWithError: () => FlowStep<V> | undefined;
  readonly clearDraft: () => void;
}

export interface UseFlowOptions {
  readonly draft?: FlowDraft | undefined;
  /** Quem só lê: navega por todos os passos sem validar (R3 da revisão do !11135). */
  readonly readOnly?: boolean;
}

type HistoryState = { readonly __TSR_index?: number };

// Passo com `draft: false` guarda dado que o rascunho recusa (documento): ele e os seguintes voltam a fazer no F5.
function restorable<V extends FieldValues>(definition: FlowDefinition<V>, completed: readonly string[]): readonly string[] {
  const cut = definition.steps.findIndex((step) => step.draft === false);
  if (cut < 0) return completed;
  const kept = new Set(definition.steps.slice(0, cut).map((step) => step.id));
  return completed.filter((id) => kept.has(id));
}

/**
 * Arquétipo Fluxo (U39 §1) sobre um formulário RHF: passos declarados, validação por passo, passo atual
 * na URL (`?passo=`) e só alcançável com os anteriores concluídos. Mudar um campo de um passo reabre
 * ele e os seguintes. A tela é o `FlowScreen`.
 */
export function useFlow<V extends FieldValues>(definition: FlowDefinition<V>, form: UseFormReturn<V>, options: UseFlowOptions = {}): FlowModel<V> {
  const first = definition.steps[0];
  if (!first) throw new Error(`fluxo ${definition.id}: sem passos`);
  const param = definition.param ?? FLOW_PARAM;
  const readOnly = options.readOnly ?? false;
  const draft = readOnly ? undefined : options.draft;
  const navigate = useNavigate();
  const router = useRouter();
  const search: Record<string, unknown> = useSearch({ strict: false });
  const [saved] = useState(() => draft?.read());
  const [completed, setCompleted] = useState<ReadonlySet<string>>(() => new Set(restorable(definition, saved?.completed ?? [])));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<FlowStepError | undefined>(undefined);
  const [errorEpoch, setErrorEpoch] = useState(0);
  const abort = useRef<AbortController | null>(null);
  // Cada passo com o índice do histórico em que esteve por último: o sheet na URL (`?sheet=`) empilha uma entrada
  // e o fechar a troca, então o mesmo passo ocupa duas e o "Voltar" precisa recuar pela distância real.
  const history = useRef<{ id: string; at: number }[]>([]);
  const historyIndex = () => (router.history.location.state as HistoryState).__TSR_index ?? 0;
  const [initialAt] = useState(historyIndex);
  const last = useRef<{ id: string | undefined; at: number }>({ id: undefined, at: initialAt });
  const backHandled = useRef(false);

  useLayoutEffect(() => {
    if (saved && Object.keys(saved.values).length > 0) form.reset(mergeDraft(form.getValues(), saved.values), { keepDefaultValues: true });
  }, [saved, form]);

  const values = useWatch({ control: form.control }) as V;
  const steps = visibleSteps(definition, values);
  const reachable = (id: string) => (readOnly ? steps.some((step) => step.id === id) : isReachable(steps, id, completed));
  const requested = typeof search[param] === 'string' ? search[param] : undefined;
  const requestedOk = requested !== undefined && reachable(requested);
  const fallback = requested === undefined ? steps[0] : readOnly ? steps[0] : firstIncomplete(steps, completed);
  const current = (requestedOk ? steps.find((step) => step.id === requested) : fallback) ?? first;
  const index = Math.max(0, steps.findIndex((step) => step.id === current.id));

  const [track, setTrack] = useState({ id: current.id, index, direction: 'forward' as 'forward' | 'back' });
  if (track.id !== current.id) setTrack({ id: current.id, index, direction: index >= track.index ? 'forward' : 'back' });

  const go = useCallback(
    (id: string, replace = false) => navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, [param]: id }), replace }),
    [navigate, param],
  );

  // URL pedindo um passo que ainda não pode abrir (F5 sem rascunho, link copiado): vai ao primeiro que falta.
  useEffect(() => {
    if (requested !== undefined && requested !== current.id) void go(current.id, true);
  }, [requested, current.id, go]);

  const reopen = useCallback(
    (stepId: string) => {
      const from = definition.steps.findIndex((step) => step.id === stepId);
      if (from < 0) return;
      const reopened = new Set(definition.steps.slice(from).map((step) => step.id));
      setCompleted((previous) => ([...previous].some((id) => reopened.has(id)) ? new Set([...previous].filter((id) => !reopened.has(id))) : previous));
    },
    [definition],
  );

  // O `onBack` limpa dado do passo deixado; o destino e os seguintes precisam ser refeitos.
  const runBack = (left: FlowStep<V>, to: FlowStep<V>) => {
    if (!left.onBack) return;
    left.onBack(form.getValues(), { form, to: to.id });
    reopen(to.id);
  };

  // Pilha das entradas do histórico que o fluxo criou (o "Voltar" desfaz o avanço pelo próprio histórico)
  // e o `onBack` do voltar do navegador: índice do histórico menor e passo anterior ao deixado.
  useLayoutEffect(() => {
    const previous = last.current;
    const at = historyIndex();
    const stack = history.current;
    last.current = { id: current.id, at };
    const same = previous.id === current.id;
    const popped = !same && previous.id !== undefined && at < previous.at;
    if (popped) {
      const position = stack.findLastIndex((entry) => entry.id === current.id);
      if (position >= 0) stack.length = position + 1;
      else stack.length = 0;
    }
    const top = stack.at(-1);
    if (top?.id === current.id) top.at = at;
    else stack.push({ id: current.id, at });
    if (same) return;
    const handled = backHandled.current;
    backHandled.current = false;
    if (!popped || handled) return;
    const left = definition.steps.find((step) => step.id === previous.id);
    // Em leitura o `onBack` não roda (limparia dado na tela), como no "Voltar" (R6 da revisão do !11135).
    if (!readOnly && left && definition.steps.indexOf(current) < definition.steps.indexOf(left)) runBack(left, current);
  });

  // Digitar num campo reabre o passo dono dele e os seguintes (o `beforeNext` precisa rodar de novo). O
  // `setValue` não reabre: ele também serve ao `beforeNext` e ao rascunho; quem grava por fora chama `reopen`.
  useEffect(() => {
    const subscription = form.watch((_, { name, type }) => {
      if (type !== 'change' || !name) return;
      const owner = stepOfField(definition.steps, name);
      if (owner) reopen(owner.id);
    });
    return () => subscription.unsubscribe();
  }, [form, definition, reopen]);

  useEffect(() => {
    if (!draft) return;
    const write = () => draft.write({ values: form.getValues(), completed: [...completed] });
    write();
    const subscription = form.watch(() => write());
    return () => subscription.unsubscribe();
  }, [draft, form, completed]);

  useEffect(() => () => abort.current?.abort(), []);

  const next = async (): Promise<boolean> => {
    if (pending) return false;
    const step = current;
    setError(undefined);
    if (readOnly) {
      const target = steps[index + 1];
      if (target) await go(target.id);
      return target !== undefined;
    }
    const fields = step.fields ?? [];
    const valid = fields.length === 0 || (await form.trigger(fields, { shouldFocus: false }));
    if (!valid) {
      setErrorEpoch((epoch) => epoch + 1);
      return false;
    }
    if (step.beforeNext) {
      const controller = new AbortController();
      abort.current = controller;
      setPending(true);
      try {
        const result: unknown = await step.beforeNext(form.getValues(), { form, signal: controller.signal });
        if (result === false) return false;
      } catch (caught) {
        const apiError = ApiError.from(caught);
        if (!apiError.isCanceled) {
          setError({ error: apiError, unmatched: applyApiFieldErrors(form, apiError.fieldErrors) });
          setErrorEpoch((epoch) => epoch + 1);
        }
        return false;
      } finally {
        if (abort.current === controller) abort.current = null;
        setPending(false);
      }
    }
    const done = new Set(completed).add(step.id);
    // O avanço lê o `completed` novo no mesmo render da URL nova; sem o flush, a URL chega antes e volta.
    flushSync(() => setCompleted(done));
    const after = visibleSteps(definition, form.getValues());
    const target = after[after.findIndex((item) => item.id === step.id) + 1];
    if (target) await go(target.id);
    return true;
  };

  const back = (count?: number) => {
    const distance = count === undefined ? backStepsOf(current, form.getValues()) : Math.max(1, Math.trunc(count));
    const target = steps[Math.max(0, index - distance)];
    if (!target || target.id === current.id) return;
    setError(undefined);
    if (!readOnly) flushSync(() => runBack(current, target));
    backHandled.current = true;
    const stack = history.current;
    const entry = stack.length > distance ? stack.at(-1 - distance) : undefined;
    const delta = entry ? historyIndex() - entry.at : 0;
    if (entry?.id === target.id && delta > 0 && router.history.canGoBack()) router.history.go(-delta);
    else void go(target.id);
  };

  const canGoTo = (id: string) => reachable(id) || steps.findIndex((step) => step.id === id) < index;

  return {
    definition,
    form,
    param,
    steps,
    current,
    index,
    total: steps.length,
    isFirst: index === 0,
    isReview: current.kind === 'review',
    readOnly,
    completed,
    direction: track.direction,
    pending,
    error,
    errorEpoch,
    next,
    back,
    goTo: (id) => {
      if (id !== current.id && canGoTo(id)) void go(id);
    },
    canGoTo,
    reopen,
    // `getFieldState` lê o erro vivo do controle; o `formState` capturado no render estaria velho no handler.
    stepWithError: () => steps.find((step) => step.fields?.some((field) => form.getFieldState(field).invalid)),
    clearDraft: () => draft?.clear(),
  };
}
